import React, { useState } from 'react';
import logo from '../../assets/images/logo.jpg';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ShieldIcon from '@mui/icons-material/Shield';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BarChartIcon from '@mui/icons-material/BarChart';
import PersonIcon from '@mui/icons-material/Person';
import PeopleIcon from '@mui/icons-material/People';
import LockIcon from '@mui/icons-material/Lock';
import MenuIcon from '@mui/icons-material/Menu';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

import InsightsIcon from '@mui/icons-material/Insights';
import SecurityIcon from '@mui/icons-material/Security';
import BusinessIcon from '@mui/icons-material/Business';
import AssessmentIcon from '@mui/icons-material/Assessment';

import HubIcon from '@mui/icons-material/Hub';
import PolicyIcon from '@mui/icons-material/Policy';

const Sidebar = ({ activeTab, onTabChange, onLogout, user, isMobileOpen, setMobileOpen }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const roles = user?.roles || [];
  const isAdmin = roles.includes('admin') || roles.includes('sys_admin');

  // Role Groups for Granular Visibility
  const riskRoles = ['risk_owner', 'risk_champion', 'risk_admin', 'manager', 'director'];
  const sheRoles = ['she_officer', 'she_manager'];
  const allBizRoles = [...riskRoles, ...sheRoles];
  const adminRoles = ['admin', 'sys_admin'];

  const allMenuItems = [
    { id: 'dashboard', name: 'Dashboard', icon: <DashboardIcon />, roles: ['*'] },
    { id: 'risks', name: 'Risk Registers', icon: <ShieldIcon />, roles: riskRoles },
    { id: 'assessment', name: 'Risk Assessment', icon: <AssessmentIcon />, roles: riskRoles },
    { id: 'controls', name: 'Controls & Treatment', icon: <SecurityIcon />, roles: riskRoles },
    { id: 'she', name: 'SHE Compliance', icon: <CheckCircleIcon />, roles: allBizRoles },
    { id: 'losses', name: 'Loss Events', icon: <BarChartIcon />, roles: allBizRoles },
    { id: 'insurance', name: 'Insurance Claims', icon: <PolicyIcon />, roles: allBizRoles },
    { id: 'montecarlo', name: 'Monte Carlo Analysis', icon: <InsightsIcon />, roles: allBizRoles },
    { id: 'bcm', name: 'Business Continuity', icon: <HubIcon />, roles: riskRoles },
    { id: 'reports', name: 'Analysis & Reports', icon: <BarChartIcon />, roles: allBizRoles },
    { id: 'profile', name: 'Profile', icon: <PersonIcon />, roles: ['*'] },
    { id: 'users', name: 'User Management', icon: <PeopleIcon />, roles: adminRoles },
    { id: 'Sessions & Logs', name: 'Sessions & Logs', icon: <AdminPanelSettingsIcon />, roles: adminRoles },
  ];

  const menuItems = allMenuItems.filter(item => {
    // 1. Database-driven permissions (ABSOLUTE PRIORITY)
    if (user?.permissions) {
      const dbPerm = user.permissions.find(p => 
        p.section_name.toLowerCase() === item.name.toLowerCase() || 
        p.section_name.toLowerCase() === item.id.toLowerCase()
      );
      
      // If DB explicitly says YES or NO, respect it even for admins
      if (dbPerm) {
        return parseInt(dbPerm.can_view) === 1;
      }
    }

    // 2. Sys Admin sees everything IF no specific DB record exists
    if (roles.includes('sys_admin') || roles.includes('System Administrator')) return true;

    // 3. Default Role-based filter
    return item.roles.includes('*') || item.roles.some(r => roles.includes(r));
  });

  return (
    <aside className={`sidebar glass ${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'mobile-open' : ''}`}>
      <div className="sidebar-header" style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        marginBottom: '40px',
        padding: isCollapsed ? '0 8px' : '0'
      }}>
        {!isCollapsed && (
          <div className="logo" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img src={logo} alt="MSU Logo" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
            <h2 className="gradient-text" style={{ fontSize: '20px' }}>MSU RiskX</h2>
            <span style={{ fontSize: '10px', color: 'var(--text-muted)', ml: 1 }}>v2.0.1m</span>
          </div>
        )}
        <div style={{ display: 'flex', gap: '8px' }}>
          {/* Mobile Close Button - visibility controlled by CSS */}
          <button 
            className="mobile-close-btn"
            onClick={() => setMobileOpen && setMobileOpen(false)}
            style={{ 
              background: 'transparent', 
              border: 'none', 
              color: 'var(--text-secondary)', 
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
          
          <button 
            className="desktop-collapse-btn"
            onClick={() => setIsCollapsed(!isCollapsed)}
            style={{ 
              background: 'transparent', 
              border: 'none', 
              color: 'var(--text-secondary)', 
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <MenuIcon />
          </button>
        </div>
      </div>
      
      <nav className="sidebar-nav" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {menuItems.map((item) => (
          <div 
            key={item.id} 
            className={`nav-link ${activeTab === item.id ? 'active' : ''}`} 
            onClick={() => {
              onTabChange(item.id);
              if (setMobileOpen) setMobileOpen(false);
            }}
            title={isCollapsed ? item.name : ''}
          >
            <span style={{ fontSize: '20px', display: 'flex' }}>{item.icon}</span>
            {!isCollapsed && <span className="nav-text">{item.name}</span>}
          </div>
        ))}
      </nav>

      <div className="sidebar-footer" style={{ marginTop: 'auto', borderTop: '1px solid var(--border-color)', paddingTop: '24px' }}>
        <div 
          className="nav-link" 
          style={{ color: '#ef4444' }} 
          onClick={onLogout}
          title={isCollapsed ? 'Logout' : ''}
        >
          <LockIcon />
          {!isCollapsed && <span className="nav-text">Logout</span>}
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
